import { useState, useCallback } from 'react';

interface SpecsBuilderProps {
  specs: Record<string, string>;
  onChange: (specs: Record<string, string>) => void;
}

const TEMPLATES: Record<string, Record<string, string>> = {
  smartphone: {
    Display: '',
    Processor: '',
    RAM: '',
    Storage: '',
    Battery: '',
    Camera: '',
    'OS': '',
    Connectivity: '',
  },
  laptop: {
    Display: '',
    Processor: '',
    RAM: '',
    Storage: '',
    Graphics: '',
    Battery: '',
    Weight: '',
    'OS': '',
  },
  audio: {
    'Driver Size': '',
    'Frequency Response': '',
    Impedance: '',
    Connectivity: '',
    'Battery Life': '',
    'Noise Cancellation': '',
  },
};

export default function SpecsBuilder({ specs, onChange }: SpecsBuilderProps) {
  const [entries, setEntries] = useState<Array<{ key: string; value: string }>>(
    Object.entries(specs).map(([key, value]) => ({ key, value })),
  );

  const updateEntries = useCallback(
    (newEntries: Array<{ key: string; value: string }>) => {
      setEntries(newEntries);
      const specObj: Record<string, string> = {};
      for (const entry of newEntries) {
        if (entry.key.trim()) {
          specObj[entry.key.trim()] = entry.value;
        }
      }
      onChange(specObj);
    },
    [onChange],
  );

  const addRow = useCallback(() => {
    updateEntries([...entries, { key: '', value: '' }]);
  }, [entries, updateEntries]);

  const removeRow = useCallback(
    (index: number) => {
      updateEntries(entries.filter((_, i) => i !== index));
    },
    [entries, updateEntries],
  );

  const updateRow = useCallback(
    (index: number, field: 'key' | 'value', val: string) => {
      const updated = [...entries];
      updated[index] = { ...updated[index], [field]: val };
      updateEntries(updated);
    },
    [entries, updateEntries],
  );

  const loadTemplate = useCallback(
    (template: Record<string, string>) => {
      const newEntries = Object.entries(template).map(([key, value]) => ({
        key,
        value: specs[key] || value,
      }));
      updateEntries(newEntries);
    },
    [specs, updateEntries],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="label-field">Product Specifications</label>
        <div className="flex gap-2">
          <select
            onChange={(e) => {
              const template = TEMPLATES[e.target.value];
              if (template) loadTemplate(template);
            }}
            className="input-field text-sm"
            defaultValue=""
          >
            <option value="" disabled>Load template</option>
            <option value="smartphone">Smartphone</option>
            <option value="laptop">Laptop</option>
            <option value="audio">Audio</option>
          </select>
          <button type="button" onClick={addRow} className="btn-secondary text-sm">
            <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Row
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {entries.map((entry, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={entry.key}
              onChange={(e) => updateRow(index, 'key', e.target.value)}
              placeholder="Specification name"
              className="input-field w-48"
            />
            <input
              type="text"
              value={entry.value}
              onChange={(e) => updateRow(index, 'value', e.target.value)}
              placeholder="Value"
              className="input-field flex-1"
            />
            <button
              type="button"
              onClick={() => removeRow(index)}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {entries.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No specifications added yet</p>
          <button type="button" onClick={addRow} className="mt-2 text-sm text-primary-600 hover:underline">
            Add your first specification
          </button>
        </div>
      )}
    </div>
  );
}
